
<template>
  <div class="" :class="[isMultiSubmit? 'p-0' : 'p-6']">
    <h1 v-if="!isMultiSubmit" class="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-6 flex items-center">
      <button
        @click="$router.push(`/configworkspace/?tab=${formField.config_type}`)"
        class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4 mr-1"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clip-rule="evenodd"
          />
        </svg>
        Back to Configs
      </button>
      {{ isNewConfig ? "Add Config" : "Edit Config" }}
      <div class="relative ml-2">
        <button
            type="button"
            class="bg-transparent hover:bg-transparent"
            @click="showTutorialMenu = !showTutorialMenu"
            aria-label="Tutorials"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark-icon lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>

        <!-- Click-away backdrop -->
        <div v-if="showTutorialMenu" class="fixed inset-0 z-40" @click="showTutorialMenu = false"></div>

        <!-- Tutorial picker menu -->
        <div
          v-if="showTutorialMenu"
          class="absolute z-50 left-0 top-full mt-2 w-56 glass-card rounded-lg shadow-lg py-1 font-normal overflow-hidden"
        >
          <p class="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Start a walkthrough</p>
          <button type="button" class="block w-full text-left px-4 py-2 text-sm font-medium bg-transparent text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" @click="runTutorial('collectorconfig')">Collector Config</button>
          <button type="button" class="block w-full text-left px-4 py-2 text-sm font-medium bg-transparent text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" @click="runTutorial('alloyconfig', 'windows')">Alloy Config - Windows</button>
          <button type="button" class="block w-full text-left px-4 py-2 text-sm font-medium bg-transparent text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" @click="runTutorial('alloyconfig', 'linux')">Alloy Config - Linux</button>
        </div>
      </div>
    </h1>

    <div v-if="loading" class="flex flex-col items-center justify-center py-20">
      <svg
        class="animate-spin h-10 w-10 text-indigo-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z"
        />
      </svg>
      <p class="mt-3 text-sm text-gray-600 dark:text-gray-300">Loading...</p>
    </div>

    <HowToConfigs v-if="showHowToPage"
      :show-how-to-page="showHowToPage"
      :external-navigation="true"
      @show-how-to-close="handleShowHowToPage"
    />

    <div
      v-else-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Collector Config/Synthetic Transaction -->
        
    <SyntheticTransactionModal
      v-if="formField.config_type === 'collectorconfig' && !isGeneralFileUpload"
      :is-visible="displayNetResponse"
      v-model="collectorConfigField"
      :field-labels="collectorfieldLabels"
      :protocols="protocols"
      :config-type="formField.config_type"
      :is-general-file-upload="isGeneralFileUpload"
      :can-save="isStaff || canChangeConfig || canAddConfig"
      :errors="fieldErrors"
      @protocol-change="onProtocolChange"
      @save="saveSyntheticTransactions"
      @cancel="hideNetResponse"
      @delete="deleteNetResponseItem"
    />


    <!-- Validation Success Modal -->
    <div 
      v-if="validationSuccessful" 
      class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40"
    >
      <div class="glass-card p-6 mb-2 w-[65%] shadow-xl z-50 overflow-auto">
        <div class="flex justify-end">
          <button
            @click="closeValidationModal"
            class="hover:bg-white dark:hover:bg-gray-900"
          >
            <svg xmlns="http://www.w3.org/2000/svg" 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              stroke-linecap="round" 
              stroke-linejoin="round" 
              class="lucide lucide-x-icon lucide-x"><path 
              d="M18 6 6 18"/><path d="m6 6 12 12"/>
            </svg>
          </button>
        </div>
        <div class="flex flex-col items-center justify-center h-full">

          <div class="mb-6">
            <svg 
              class="w-20 h-20 text-green-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h2 class="font-semibold mb-4 text-gray-800 dark:text-gray-200 text-2xl text-center">
            Collector Validation Successful
          </h2>
          
          <p class="text-gray-600 dark:text-gray-50 text-center mb-8 max-w-md">
            Your collector configuration has been validated successfully and is ready to use.
          </p>

          <p v-if="shouldShowSaveButton" class="text-gray-600 dark:text-gray-50 text-center mb-8 max-w-md">
            You can proceed and save your changes.
          </p>

          <div class="flex gap-4">
            <button
              type="button"
              @click="closeValidationModal"
              class="btn border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
        
    <!-- Validation Failure Modal -->
    <div 
      v-if="validationFailed" 
     class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40"
      @click.self="closeValidationModal"
    >
      <div class="glass-card p-6 mb-2 w-[65%] shadow-xl z-50 flex flex-col">
        <div class="flex justify-end flex-shrink-0">
          <button
            @click="closeValidationModal"
            class="hover:bg-white dark:hover:bg-gray-900"
          >
            <svg xmlns="http://www.w3.org/2000/svg" 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              stroke-linecap="round" 
              stroke-linejoin="round" 
              class="lucide lucide-x-icon lucide-x"><path 
              d="M18 6 6 18"/><path d="m6 6 12 12"/>
            </svg>
          </button>
        </div>
        
        <div class="flex flex-col py-8 flex-shrink-0">
          <div class="flex justify-center mb-6">
            <div class="rounded-full bg-red-100 dark:bg-red-900 p-3">
              <svg 
                class="w-16 h-16 text-red-600 dark:text-red-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  stroke-linecap="round" 
                  stroke-linejoin="round" 
                  stroke-width="2" 
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>

          <h2 class="font-semibold mb-2 text-gray-800 dark:text-gray-200 text-2xl text-center">
            Collector Validation Failed
          </h2>
          
          <div v-if="validationMessage" class="overflow-auto flex-1 min-h-0 w-[90%] max-h-[40%] mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded p-4">
            <p class="font-semibold text-left pl-4">
              {{ validationMessage }}.
            </p>
          </div>
        </div>

        <div v-if="validationErrorMessages.length > 0" class="overflow-auto flex-1 min-h-0 w-[90%] max-h-[40%] mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded p-4">
          <div class="my-2" v-for="(message, index) in validationErrorMessages" :key="index">
            <p class="font-semibold text-left pl-4">
              {{ message }}
            </p>
          </div>
        </div>

        <div class="flex justify-center gap-4">
          <button
            type="button"
            @click="closeValidationModal"
            class="btn bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Content Area -->
    <template v-if="displayConfig">
      <div 
        class="flex items-center justify-center inset-0 fixed backdrop-blur-sm bg-black/50 z-40"
      >
        <div
          class="glass-card p-6 mb-2 w-full sm:w-4/5 md:w-3/4 min-h-64 sm:min-h-96 shadow-xl z-50">
          <h2 class="font-semibold mb-4 text-gray-800 dark:text-gray-200 capitalize">
            Content
          </h2>
          <div>
            <label
              for="content"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              ></label
            >
            <textarea
              type="textarea"
              id="content"
              v-model="formField.content"
              :class="[ 'pt-3 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['content']
                    ? 'ring-2 ring-red-500  focus:ring-red-500'
                    : 'focus:ring-blue-500', formField.config_type === 'workspaceconfig' ? 'h-[350px]' : 'h-[500px]' 
              ]"
            ></textarea>
            <FieldError :message="fieldErrors['content']"/>
          </div>

          <div class="flex justify-end pt-6">
            <button
              type="button"
              @click="toggleConfig"
              class="ml-2 btn rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </template>

    <form v-else-if="!loading" @submit.prevent="saveConfig" class="space-y-6 glass-card-lite p-6 ">
      <div class="space-y-6 p-6 glass-card-lite">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div v-if="!isMultiSubmit">
            <label
              for="config_type"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              >Config Type <span class="text-red-500 font-bold text-base">*</span></label
            >
            <GlassSelect
              id="config_type"
              v-model="formField.config_type"
              :options="isStaff ? configTypes : nonStaffconfigTypes"
              placeholder="Select a Config Type"
              :valueKey="'key'"
              :labelKey="'label'"
              :disabled="!isNewConfig"
              @focus="clearFieldError('config_type')"
              :error="!!fieldErrors['config_type']"
            />
            <FieldError :message="fieldErrors['config_type']"/>
          </div>

          <div v-if="!isMultiSubmit">
            <label for="operating_system" class="block text-sm font-medium text-gray-700 dark:text-white">Operating System <span class="text-red-500 font-bold text-base">*</span></label>
            <GlassSelect
              id="operating_system"
              v-model="formField.operating_system"
              @focus="clearFieldError('operating_system')"
              :disabled="!isNewConfig"
              :options="os_options"
              valueKey="os_type"
              labelKey="os_type"
              placeholder="Select an Operating System"
              :error="!!fieldErrors['operating_system']"
            />
            <FieldError :message="fieldErrors['operating_system']"/>
          </div>

          <div v-if="isWindows && !isMultiSubmit">
            <label for="space_type" class="block text-sm font-medium text-gray-700 dark:text-white">Space Type <span class="text-red-500 font-bold text-base">*</span></label>
            <GlassSelect
              id="space_type"
              v-model="formField.space_type"
              @focus="clearFieldError('space_type')"
              :options="space_type_options"
              valueKey="key"
              labelKey="label"
              placeholder="Select a Space Type"
              :disabled="!isNewConfig"
              :error="!!fieldErrors['space_type']"
            />
            <FieldError :message="fieldErrors['space_type']"/>
          </div>

          <div v-if="!isMultiSubmit">
            <label
              for="organization"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              >Organisation <span class="text-red-500 font-bold text-base">*</span></label
            >
            <GlassSelect
              id="organization"
              v-model="formField.organization"
              :options="orgs"
              placeholder="Select an organisation"
              :disabled="!isNewConfig"
              @change="formField.agent = null; formField.group = null"
              @focus="clearFieldError('organization')"
              :error="!!fieldErrors['organization']"
            />
            <FieldError :message="fieldErrors['organization']"/>
          </div>

          <div v-if="isStaff">
            <label for="file"  class="block text-sm font-medium text-gray-700 dark:text-white">
              File
            </label>

            <input
              type="file"
              id="file"
              ref="file"
              accept=".conf,.yml,.yaml,.toml"
              @change="handleFileUpload"
              class="hidden"
            />
            <div class="flex items-center gap-2">
              <div class="flex items-center flex-1 min-w-0 dark:bg-gray-700 bg-gray-200 rounded">
                <button
                  id="choose_file"
                  type="button"
                  @click="$refs.file.click()"
                  :disabled="isProcessingFile"
                  class="shrink-0 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600"
                >
                  Choose File
                </button>
                <span v-if="isProcessingFile" class="flex items-center px-3 text-sm text-gray-500 dark:text-gray-300">
                  <svg class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z" />
                  </svg>
                  Validating file...
                </span>
                <a v-else-if="formField.file" class="px-3 text-sm truncate hover:no-underline dark:hover:text-indigo-300 dark:text-white" :href="formField.file" target="_blank">{{ formField.file?.name || formField.file }}</a>
                <span v-else class="px-3 text-sm text-gray-500 dark:text-gray-400">No file chosen</span>
              </div>
              <button
                v-if="formField.config_type === `collectorconfig`"
                id="validate_collector_file"
                type="button"
                @click="validateCollectorConfigFile"
                :disabled="!formField.file || isValidatingConfig || isProcessingFile"
                class="shrink-0 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600"
              >
                <span v-if="isValidatingConfig" class="flex items-center justify-center">
                  <svg
                    class="animate-spin h-5 w-5 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z"
                    />
                  </svg>
                  Validating...
                </span>
                <span v-else>Validate Collector File</span>
              </button>
            </div>
            <FieldError :message="fieldErrors['file']"/>
          </div>

          <div v-if="!isMultiSubmit">
            <label
              for="group"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              >Agent Group</label
            >
            <GlassSelect
              id="group"
              v-model="formField.group"
              :options="filteredGroups"
              placeholder="Select an Agent Group"
              @focus="clearFieldError('group')"
              :error="!!fieldErrors['group']"
            />
            <FieldError :message="fieldErrors['group']"/>
          </div>

          <div v-if="!isMultiSubmit">
            <label
              for="agent"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              >Agent</label
            >
            <GlassSelect
              id="agent"
              v-model="formField.agent"
              :options="filteredAgents"
              placeholder="Select an Agent"
              :valueKey="'licensekey'"
              :labelKey="'licensekey'"
              @focus="clearFieldError('agent')"
              :error="!!fieldErrors['agent']"
            />
            <FieldError :message="fieldErrors['agent']"/>
          </div>
        </div>
        <FieldError :message="fieldErrors['non_field_errors']"/>


      </div>

      <div v-if="!isNewConfig && formField.config_type === `collectorconfig`">
        <h2 class="font-semibold mb-4 text-gray-800 dark:text-gray-200 capitalize">Toggle Metrics Collection</h2>
        <div 
          class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
          <input 
            v-model="workspaceMetricsToggleForCollector.enabled"
            @change="syncBothToggles"
            id="toggle"
            type="checkbox"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
          />
          <label 
            for="toggle" 
            class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"
          ></label>
        </div>

        <input 
          v-model="workspaceMetricsToggleForWorkspace.enabled"
          type="checkbox"
          style="display: none;"
          ref="hiddenWorkspaceToggle"
        />
      </div>  

      <InterfaceMonitoringSection
        v-model="workspaceConfigField"
        :workspace-file="workspaceFile"
        :protocols="protocols"
        :config-type="formField.config_type"
        :is-staff="isStaff"
        :is-new-config="isNewConfig"
        :is-general-file-upload="isGeneralFileUpload"
        :is-workspace-file-upload="isWorkspaceFileUpload"
        :workspace-metrics-enabled="workspaceMetricsToggleForWorkspace.enabled"
        :errors="fieldErrors"
        @file-change="handleWorkspaceFileUpload"
        @clear-error="clearFieldError"
      />

      <ActivityMonitoringSection
        v-model="workspaceConfigField"
        :config-type="formField.config_type"
        :is-staff="isStaff"
        :is-general-file-upload="isGeneralFileUpload"
        :is-workspace-file-upload="isWorkspaceFileUpload"
        :errors="fieldErrors"
        @clear-error="clearFieldError"
      />

      <!-- Launcher Config -->
      <LauncherConfigSection
        v-model="launcherConfigField"
        :field-labels="fieldLabels"
        :definitions="definitions"
        :config-type="formField.config_type"
        :space-type="formField.space_type"
        :is-general-file-upload="isGeneralFileUpload"
        :errors="fieldErrors"
        @clear-error="clearFieldError"
      />

        <!-- Alloy Config -->
      <AlloyConfigSection
        v-model="alloyConfigField"
        :config-type="formField.config_type"
        :is-general-file-upload="isGeneralFileUpload"
        :os-type="osType"
        :errors="fieldErrors"
        @clear-error="clearFieldError"
        @start-tutorial="startSectionTutorial"
      />

        <!-- Hardware Monitoring --> 
      
      <HardwareMonitoringSection
        v-model="machineResponseField"
        :labels="machineResponseLabel"
        :categories="machineResponseCategories"
        :definitions="definitions"
        :config-type="formField.config_type"
        :is-general-file-upload="isGeneralFileUpload"
        :metrics-enabled="workspaceMetricsToggleForCollector.enabled"
      />

        <!-- Synthetic Transactions --> 
      <SyntheticTransactionsSection
        :transactions="netresponses"
        :config-type="formField.config_type"
        :is-general-file-upload="isGeneralFileUpload"
        :metrics-enabled="workspaceMetricsToggleForCollector.enabled"
        :can-add="isStaff || canChangeConfig || canAddConfig"
        @edit="editNetResponse"
        @toggle="toggleTransaction"
        @remove="removeTransaction"
        @add-new="displayNetResponse = true"
      />
      
      <DatabaseConfigSection
        v-model="databaseConfigField"
        :is-staff="isStaff"
        :config-type="formField.config_type"
        :space-type="formField.space_type"
        :is-general-file-upload="isGeneralFileUpload"
        :errors="fieldErrors"
        @clear-error="clearFieldError"
      />
      
    
      <div 
        v-if="isStaff"
        class="space-x-3">
        <button 
          class="btn border border-transparent shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          type="button"
          @click="toggleConfig"
          >
          View Configuration
        </button>
      </div>


      <div class="flex justify-end space-x-3">
        <button
          type="button"
          @click="$router.push(`/configworkspace/?tab=${formField.config_type}`)"
          class="btn border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Back
        </button>
        <button 
          v-if="isStaff || canChangeConfig || canAddConfig"
          id="submit_config"
          type="submit"
          :disabled="isValidatingConfig"
          class="btn border border-transparent text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
        >
          <div v-if="isValidatingConfig" class="flex items-center justify-center">
            <svg
              class="animate-spin h-5 w-5 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z"
              />
            </svg>
            <span class="text-sm font-medium text-white">Validating...</span>
          </div>
          <span v-else class="text-sm font-medium text-white">{{ isNewConfig ? "Create Config" : "Save Changes" }}</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { createConfig, getConfigs, updateConfig, validateConfig } from "@/services/configService";
import { createWorkspaceConfig, getWorkspaceConfigs, updateWorkspaceConfig } from "@/services/workspaceConfigService";
import { createLauncherConfig, getLauncherConfigs, updateLauncherConfig } from "@/services/launcherConfigService";
import { getAlloyConfigs, createAlloyConfig, updateAlloyConfig } from "@/services/alloyConfigService";
import { createNetResponse, getNetResponses, updateNetResponse, deleteNetResponse } from "@/services/netResponseService";
import { createMachineResponse, getMachineResponses, updateMachineResponse } from "@/services/machineResponseService";
import { updateMetricsToggle, getMetricsToggle, createMetricsToggle } from "@/services/workspaceMetricsToggleService";
import { createDatabaseConfig, getDatabaseConfigs, updateDatabaseConfig } from "@/services/databseConfigService";
import { getOrgs } from "@/services/wsOrgService";
import { getAgentGroups } from "@/services/agentGroupService";
import { getAgents } from "@/services/agentService";
import { useStore } from "@/store";
import FieldError from "@/components/FieldError.vue";
import HowToConfigs from "./HowToConfigs.vue";
import HowToSite from "./HowToSite.vue";
import { useTutorial } from "@/composables/tutorial";
import { collectorSteps, alloyFullSteps, ALLOY_SECTIONS } from "@/composables/tutorialSteps";
import DatabaseConfigSection from "@/components/Workspace/DatabaseConfigSection.vue";
import HardwareMonitoringSection from "@/components/Workspace/HardwareMonitoringSection.vue";
import SyntheticTransactionsSection from "@/components/Workspace/SyntheticTransactionsSection.vue";
import LauncherConfigSection from "@/components/Workspace/LauncherConfigSection.vue";
import AlloyConfigSection from "@/components/Workspace/AlloyConfigSection.vue";
import InterfaceMonitoringSection from "@/components/Workspace/InterfaceMonitoringSection.vue";
import ActivityMonitoringSection from "@/components/Workspace/ActivityMonitoringSection.vue";
import { validateConfigFile } from "@/utils/fileSignature";
import SyntheticTransactionModal from "@/components/Workspace/SyntheticTransactionModal.vue";
import GlassSelect from "@/components/GlassSelect.vue";

export default {
  name: "ConfigDetails",
  setup() {
    const { start } = useTutorial();
    return { _start: start };
  },
  emits: ['collector-submitted', 'workspace-submitted'],
  components: {
    FieldError,
    HowToConfigs,
    DatabaseConfigSection,
    HardwareMonitoringSection,
    SyntheticTransactionsSection,
    LauncherConfigSection,
    AlloyConfigSection,
    InterfaceMonitoringSection,
    ActivityMonitoringSection,
    SyntheticTransactionsSection,
    SyntheticTransactionModal,
    GlassSelect
  },
  props: {
  isMultiSubmit: {
    type: Boolean,
    default: false
  },
  configType:{
    type: String,
    default: "workspaceconfig"
  },
  multiSubmitAgent:{
    type: String,
    default: null
  },
  multiSubmitOrg:{
    type: Number,
    default: null
  },
  multiSubmitGroup:{
    type: Number,
    default: null
  },
  multiSubmitOS:{
    type: String,
    default: null
  },
  multiSubmitSpaceType:{
    type: String,
    default: null
  },
},
  data() {
    return {
      isWorkspaceFileUpload: false,
      isGeneralFileUpload: false,

      showHowToPage: false,

      // Standard Config
      formField: {
        config_type: "",
        operating_system: "",
        space_type: "serverspace",
        checksum: "",
        content: "Leave Empty",
        file: null,
        organization: null,
        group: null,
        agent: null,
        id: null
      },

      workspaceFile: {
        file: null
      },

      // Default Configs
      workspaceConfigField: this.defaultWorkspaceConfig(),
      launcherConfigField: this.defaultLauncherConfig(),
      alloyConfigField: this.defaultAlloyConfig(),
      originalAlloyConfig: null,
      collectorConfigField: this.defaultDNSqueryConfig(),
      machineResponseField: this.defaultMachineResponse(),
      databaseConfigField: this.defaultDatabaseConfig(),


      // Data 
      displayNetResponse: false,
      isdropDownVisible: false,
      updatingImportantHost: false,
      selectedNetResponse : null,
      netresponses: [],
      inputPlugin : {},
      dataConfig: null,
      dataConfigWS: null,
      displayConfig: false,
      loading: false,
      error: null,
      fieldErrors: {},
      isNewConfig: false,
      orgs: [],
      agents: [],
      groups: [],
      protocols: ["tcp", "http", "https", "dns", "icmp"],
      os_options: [
        { os_type: "Windows" },
        { os_type: "Linux" },
      ],
      space_type_options: [
        { key: "serverspace", label: "Serverspace" },
        { key: "workspace", label: "Workspace" },
      ],
      haspreSelectedAgent: false,


      // Config Types
      configTypes : [
        { key:'workspaceconfig', label: 'Workspace Config'},
        { key:'launcherconfig', label: 'Launcher Config'},
        { key:'collectorconfig', label: 'Collector Config'},
        { key:'alloyconfig', label: 'Alloy Config'}
      ],

      nonStaffconfigTypes: [
        { key:'collectorconfig', label: 'Collector Config'},
        { key:'alloyconfig', label: 'Alloy Config'}
      ],

      // Labels
      fieldLabels: {
        urls: {
          register_url: "Register URL",
          checkin_url: "Check-in URL",
          update_url: "Update URL",
          mimir_url: "Mimir URL"
        },
        paths: {
          workspace: "Workspace Path",
          workspace_config: "Workspace Config Path",
          collector: "Collector Path",
          collector_config: "Collector Config Path",
          alloy: "Alloy Path",
          alloy_config: "Alloy Config Path",
          launcher_config: "Launcher Config Path",
          backup_dir: "Backup Directory",
          agent_license_key_file: "Agent License Key File",
          org_license_key_file: "Org License Key File",
          token_file: "Token File"
        },
        settings: {
          checkin_interval: "Check-in Interval",
          group_id: "Group ID",
          proxy_url: "Proxy URL"
        }
      },
      definitions: {
          // Collector Config  -- Hardware Monitoring
          process_bool : "Tracks CPU, memory, and process usage",
          processor_bool: "Tracks CPU performance across all cores",
          physical_disk_bool: "Monitors read/write activity and disk performance",
          logical_disk_bool: "Monitors disk space, usage, and performance",
          network_interface_bool: "Measures network traffic and errors on adapters",
          system_bool: "Provides overall system statistics such as uptime and queue length",
          memory_bool: "Reports on memory usage and faults",
          paging_file_bool: "Monitors swap (paging file) usage",
          docker_bool: "Monitors Docker containers, images, and resource usage",
          systemd_bool: "Tracks systemd unit states and service health",

          // Collector Config -- Hardware Inventory (Windows WMI)
          battery_bool: "Monitors laptop battery status, charge level, and health",
          logical_disk_inventory_bool: "Inventories logical disks: size, free space, and volume names",
          thermal_zone_bool: "Reports CPU and system thermal zone temperatures",
          disk_drive_bool: "Inventories physical disk drives, serial numbers, and status",
          location_bool: "Collects device location via the MIVU map PowerShell script",
          network_adapter_config_bool: "Reports IP-enabled network adapter configuration (MAC, description)",
          computer_system_bool: "Inventories manufacturer, model, domain, and total physical memory",
          operating_system_bool: "Inventories OS version, build number, and registration details",
          bios_bool: "Inventories BIOS version, manufacturer, and serial number",
          physical_memory_bool: "Inventories installed RAM modules: capacity, speed, and slots",
          processor_info_bool: "Inventories CPU model, core count, and clock speed",
          pnp_driver_bool: "Inventories signed PnP device drivers and their versions",

          // Launcher Config
          register_url: "Endpoint that the agent is going to use to register",
          checkin_url: "Endpoint that the agent is going to use to check updates at specified intervals",
          update_url: "Endpoint used by the agent to track its update status",
          mimir_url: "Endpoint used to send metrics to Mimir",

          workspace: "Workspace Path",
          workspace_config: "Workspace Config Path",
          collector: "Collector Path",
          collector_config: "Collector Config Path",
          alloy: "Path to the Alloy binary executable",
          alloy_config: "Path to the Alloy River config file",
          launcher_config: "Launcher Config Path",
          backup_dir: "Backup Directory Path",
          agent_license_key_file: "Agent License Key File Path",
          org_license_key_file: "Organizatiop License Key File Path",
          token_file: "Token File Path",

          checkin_interval: "Agent check-in interval",
          group_id: "Optional Agent Group ID",
          proxy_url: "Optional HTTP(S) proxy the launcher routes traffic through. Leave empty for a direct connection."
      },

      workspaceMetricsToggleForCollector: {
        enabled: true,
        config: null,
        previous_state: {}
      },
      workspaceMetricsToggleForWorkspace: {
        enabled: true,
        config: null,
        previous_state: {} 
      },

      originalConfigData: null,
      originalWorkspaceConfigField: this.defaultWorkspaceConfig(),
      originalMachineResponseField: this.defaultMachineResponse(),
      originalCollectorMetricsToggle:{
        enabled: true,
        config: null,
        previous_state: {} 
      },
      originalupdatedSyntheticTransactions: [],
      originalconfigedSyntheticTransactions: [],
      originalDeleteTransactions: [],
      originalDatabaseConfig: this.defaultDatabaseConfig(),
      originalLauncherConfig: this.defaultLauncherConfig(),
      originalnewSyntheticTransactions: [],

      updateSyntheticTransactions: [],
      newSyntheticTransactions: [],
      configedSyntheticTransactions: [],
      deletedSyntheticTransactions: [],

      canChangeConfig: false,
      canAddConfig: false, 

      databaseConfig: {},
      databaseConfigUrls: "", 
      showPassword: false,

      serverIP: "",
      domainURL: "",
      synthURL: "",
      selectedDnsQuery: null,

      validationSuccessful: false,
      validationFailed: false,
      isValidatingConfig: false,
      isProcessingFile: false,
      validationMessage: "",
      shouldShowSaveButton: false,

      validationErrorMessages: [],

      showTutorialMenu: false,
    };
  },
  computed: {
    machineResponseLabel() {
      const base = {
        process_bool: "Process",
        processor_bool: "Processor",
        logical_disk_bool: "Logical Disk",
        physical_disk_bool: "Physical Disk",
        network_interface_bool: "Network Interface",
        system_bool: "System",
        memory_bool: "Memory",
        paging_file_bool: "Paging File"
      };
      if (this.formField.operating_system?.toLowerCase() === 'linux') {
        return {
          ...base,
          docker_bool: "Docker",
          systemd_bool: "Systemd"
        };
      }
      return {
        ...base,
        // Windows-only WMI / hardware inventory blocks
        battery_bool: "Battery",
        logical_disk_inventory_bool: "Logical Disk Inventory",
        thermal_zone_bool: "Thermal Zone",
        disk_drive_bool: "Disk Drive",
        location_bool: "Location",
        network_adapter_config_bool: "Network Adapter Configuration",
        computer_system_bool: "Computer System",
        operating_system_bool: "Operating System",
        bios_bool: "BIOS",
        physical_memory_bool: "Physical Memory",
        processor_info_bool: "Processor Information",
        pnp_driver_bool: "PnP Drivers"
      };
    },
    machineResponseCategories() {
      // Sub-headings for the Hardware Monitoring section. The section component
      // only renders fields that exist in `machineResponseLabel`, so listing all
      // possible fields here is safe across operating systems.
      return [
        {
          title: "Performance Counters",
          fields: ["process_bool", "processor_bool", "system_bool", "memory_bool", "paging_file_bool"]
        },
        {
          title: "Disk & Storage",
          fields: ["logical_disk_bool", "physical_disk_bool", "logical_disk_inventory_bool", "disk_drive_bool"]
        },
        {
          title: "Network",
          fields: ["network_interface_bool", "network_adapter_config_bool"]
        },
        {
          title: "System & Hardware Inventory",
          fields: ["computer_system_bool", "operating_system_bool", "bios_bool", "processor_info_bool", "physical_memory_bool", "pnp_driver_bool"]
        },
        {
          title: "Power & Sensors",
          fields: ["battery_bool", "thermal_zone_bool"]
        },
        {
          title: "Containers & Services",
          fields: ["docker_bool", "systemd_bool"]
        },
        {
          title: "Location",
          fields: ["location_bool"]
        }
      ];
    },
    osType() {
      return this.formField.operating_system?.toLowerCase() || '';
    },
    isWindows() {
      return this.osType === 'windows';
    },
    isLinux() {
      return this.osType === 'linux';
    },
    // DatabaseConfig (output plugin) is a workspace-only item; alloy paths are
    // the serverspace-only mirror. Both gate on space_type.
    isWorkspaceSpace() {
      return this.formField.space_type === 'workspace';
    },
    isServerSpace() {
      return this.formField.space_type !== 'workspace';
    },
    isStaff(){
      return useStore.isStaff();
    },
    user() {
      return useStore.getUser();
    },
    formattedConfig() {
      return {
        ...this.formField
      };
    },
    filteredAgents() {
      if (!this.formField.organization) return [];
      return this.agents.filter(a => a.organization === this.formField.organization);
    },
    filteredGroups() {
      if (!this.formField.organization) return [];
      return this.groups.filter(g => g.organization === this.formField.organization);
    },
    collectorfieldLabels() {
      const isDNS = this.collectorConfigField.protocol === 'dns';
      const isICMP = this.collectorConfigField.protocol === 'icmp';
      const isHTTP = this.collectorConfigField.protocol === 'http' ||this.collectorConfigField.protocol === 'https';
      
      if (isDNS) {
        return {
          protocol: "Protocol",
          name: "Name",
          servers: "Servers",
          domains: "Domains",
          timeout: "Timeout"
        };
      } 
      if (isICMP) {
        return {
          protocol: "Protocol",
          name: "Name",
          urls: "URLs",
          timeout: "Timeout"
        };
      }
      if (isHTTP) {
        return {
          protocol: "Protocol",
          name: "Name",
          urls: "URLs",
          timeout: "Timeout"
        };
      } else {
        return {
          protocol: "Protocol",
          name: "Name",
          address: "Address"
        };
      }
    },

  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
    'formField.operating_system'() {
      this.applyAlloyOsDefaults();
      // Linux only supports serverspace; lock it to the default.
      if (this.isLinux) {
        this.formField.space_type = "serverspace";
      }
    },
  },
  async created() {
    const configId = this.$route.params.id;
    this.checkPermissions();
    this.loading = true;
    try {
      await this.fetchCollectorMetricsToggle();
      await this.fetchWorkspaceMetricsToggle();

      if (configId === "new") {
        this.isNewConfig = true;
        if (this.isMultiSubmit){
          this.formField.config_type = this.configType;
          this.formField.organization = this.multiSubmitOrg;
          this.formField.agent = this.multiSubmitAgent;
          this.formField.group = this.multiSubmitGroup;
          this.formField.operating_system = this.multiSubmitOS;
          if (this.multiSubmitSpaceType) {
            this.formField.space_type = this.multiSubmitSpaceType;
          }
          this.haspreSelectedAgent = Boolean(this.multiSubmitAgent);
        }
      } else {
        await this.fetchConfig(configId);
      }

      await this.fetchOrg();
      await this.fetchAgents();
      await this.fetchGroups();

      await this.loadConfigTypeSpecificData(this.formField.config_type);
      this.initializeOriginalValues();
    } finally {
      this.loading = false;
    }
  },
  methods: {

    clearFieldError(fieldName){
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
        delete this.fieldErrors['non_field_errors'];
      }
    },

    validateConfigData(){
      this.fieldErrors = {};
      if (this.formField.organization === null){
        this.fieldErrors['organization'] = "Please select an organisation"
      }
      if (this.formField.config_type === ""){
        this.fieldErrors['config_type'] = "Please select a config type"
      }
      if (this.isNewConfig && this.formField.operating_system === ""){
        this.fieldErrors['operating_system'] = "Please select an operating system"
      }
    },

    validateLauncherConfig() {
      if (this.isGeneralFileUpload || this.formField.config_type !== 'launcherconfig') return;

      const os = this.formField.operating_system?.toLowerCase();
      const isWindows = os === 'windows';

      const urlFields = ['register_url', 'checkin_url', 'update_url', 'mimir_url'];
      const pathFields = [
        'workspace', 'workspace_config', 'collector', 'collector_config',
        'launcher_config', 'backup_dir', 'agent_license_key_file',
        'org_license_key_file', 'token_file'
      ];

      for (const field of urlFields) {
        const value = this.launcherConfigField[field];
        if (!value || !value.trim()) {
          this.fieldErrors[field] = 'This field cannot be empty';
          continue;
        }
        try {
          new URL(value);
        } catch {
          this.fieldErrors[field] = 'Must be a valid URL (e.g. https://example.com)';
        }
      }

      for (const field of pathFields) {
        const value = this.launcherConfigField[field];
        if (!value || !value.trim()) {
          this.fieldErrors[field] = 'This field cannot be empty';
          continue;
        }
        const validPath = isWindows
          ? /^[A-Za-z]:[\\\/]/.test(value)
          : /^\//.test(value);
        if (!validPath) {
          this.fieldErrors[field] = isWindows
            ? 'Must be an absolute Windows path (e.g. C:\\path\\to\\file)'
            : 'Must be an absolute path (e.g. /path/to/file)';
        }
      }

      const interval = this.launcherConfigField.checkin_interval;
      if (!interval && interval !== 0) {
        this.fieldErrors['checkin_interval'] = 'This field cannot be empty';
      } else if (!Number.isInteger(Number(interval)) || Number(interval) <= 0) {
        this.fieldErrors['checkin_interval'] = 'Must be a positive whole number';
      }

      // Proxy is optional — only validate the format when a value is set.
      const proxy = this.launcherConfigField.proxy_url;
      if (proxy && String(proxy).trim()) {
        try {
          new URL(proxy);
        } catch {
          this.fieldErrors['proxy_url'] = 'Must be a valid URL (e.g. http://proxy.example.com:3128)';
        }
      }
    },

    validateAlloyConfig() {
      if (this.isGeneralFileUpload || this.formField.config_type !== 'alloyconfig') return;

      const a = this.alloyConfigField;

      const hasProxyPassword = !!a.proxy_password || !!a.proxy_password_set;
      if ((a.proxy_username || hasProxyPassword) && !String(a.proxy_url || '').trim()) {
        this.fieldErrors['proxy_url'] = 'Required when proxy credentials are set.';
      }
      if (hasProxyPassword && !String(a.proxy_username || '').trim()) {
        this.fieldErrors['proxy_username'] = a.proxy_password_set
          ? 'Required while a proxy password is stored. Use "Clear stored password" for an unauthenticated proxy.'
          : 'Required when a proxy password is set.';
      }

      if (this.isStaff) {
        if (!a.otlp_endpoint || !String(a.otlp_endpoint).trim()) {
          this.fieldErrors['otlp_endpoint'] = 'This field cannot be empty';
        } else {
          try {
            new URL(a.otlp_endpoint);
          } catch {
            this.fieldErrors['otlp_endpoint'] = 'Must be a valid URL (e.g. https://ingest.mivu.io)';
          }
        }

        if (!a.remote_write_org_id || !String(a.remote_write_org_id).trim()) {
          this.fieldErrors['remote_write_org_id'] = 'This field cannot be empty';
        }
      }

      if (a.apache_enabled && !a.apache_status_port) {
        this.fieldErrors['apache_status_port'] = 'Required when Apache monitoring is enabled.';
      }

      if (a.mssql_enabled && !a.mssql_connection_string && !a.mssql_connection_string_set) {
        this.fieldErrors['mssql_connection_string'] = 'Required when SQL Server monitoring is enabled.';
      }

      if (a.postgres_enabled && !a.postgres_dsn && !a.postgres_dsn_set) {
        this.fieldErrors['postgres_dsn'] = 'Required when PostgreSQL monitoring is enabled.';
      }

      if (a.vmware_enabled && this.isStaff) {
        if (!a.vmware_endpoint || !a.vmware_endpoint.trim()) {
          this.fieldErrors['vmware_endpoint'] = 'Required when VMware monitoring is enabled.';
        }
        if (!a.vmware_username || !a.vmware_username.trim()) {
          this.fieldErrors['vmware_username'] = 'Required when VMware monitoring is enabled.';
        }
        if (!a.vmware_password && !a.vmware_password_set) {
          this.fieldErrors['vmware_password'] = 'Required when VMware monitoring is enabled.';
        }
      }
    },

    validateWorkspaceConfig() {
      const type = this.formField.config_type;
      const fieldsVisible =
        (!this.isGeneralFileUpload && type === 'workspaceconfig') ||
        (!this.isWorkspaceFileUpload && type === 'collectorconfig');
      // crit_host / crit_proto are only rendered for staff on the manual form
      if (!fieldsVisible || !this.isStaff) return;

      const w = this.workspaceConfigField;

      if (!w.crit_host || !String(w.crit_host).trim()) {
        this.fieldErrors['crit_host'] = 'This field cannot be empty';
      }

      if (!w.crit_proto || !String(w.crit_proto).trim()) {
        this.fieldErrors['crit_proto'] = 'This field cannot be empty';
      }

      for (const key of ['activity_interval', 'idle_threshold']) {
        const value = Number(w[key]);
        if (w[key] === '' || w[key] === null || !Number.isInteger(value) || value < 1) {
          this.fieldErrors[key] = 'Enter a whole number of seconds (1 or more)';
        }
      }
    },

    validateDatabaseConfig() {
      // Database section only renders for staff on a workspace collector config
      if (!this.isStaff || this.isGeneralFileUpload) return;
      if (this.formField.config_type !== 'collectorconfig') return;
      if (this.formField.space_type !== 'workspace') return;

      const d = this.databaseConfigField;
      // Local (Alloy) needs no InfluxDB details; any saved ones are kept for switching back
      if (!d.enabled) return;

      if (!Array.isArray(d.urls) || d.urls.length === 0) {
        this.fieldErrors['urls'] = 'Add at least one database URL';
      }

      if (!d.database || !String(d.database).trim()) {
        this.fieldErrors['database'] = 'This field cannot be empty';
      }
    },


    // Launch a full config-type walkthrough from the header picker menu.
    runTutorial(type, os = null) {
      this.showTutorialMenu = false;
      this.startTutorial({ type, os });
    },

    // Launch a section-scoped Alloy tutorial from a section's (?) icon. The
    // section is already visible, so we reuse the currently selected OS.
    startSectionTutorial(section) {
      this.startTutorial({ section });
    },

    async startTutorial({ type = null, os = null, section = null } = {}) {
      // Snapshot every field a tutorial might mutate so we can restore on finish.
      const snapshot = {
        orgs: [...this.orgs],
        groups: [...this.groups],
        agents: [...this.agents],
        protocols: [...this.protocols],
        formField: { ...this.formField },
        workspaceConfigField: JSON.parse(JSON.stringify(this.workspaceConfigField)),
        launcherConfigField: JSON.parse(JSON.stringify(this.launcherConfigField)),
        alloyConfigField: JSON.parse(JSON.stringify(this.alloyConfigField)),
        machineResponseField: JSON.parse(JSON.stringify(this.machineResponseField)),
        databaseConfigField: JSON.parse(JSON.stringify(this.databaseConfigField)),
        collectorConfigField: JSON.parse(JSON.stringify(this.collectorConfigField)),
        newSyntheticTransactions: JSON.parse(JSON.stringify(this.newSyntheticTransactions)),
      };

      // Full walkthroughs drive the org/agent/group selects, so stub them with
      // deterministic tutorial data. Section runs leave those untouched.
      const isFullWalkthrough = Boolean(type);
      if (isFullWalkthrough) {
        this.orgs = [{ id: 1, name: 'Tutorial Organisation' }];
        this.agents = [{ id: 1, licensekey: '09qwerT5Y6uiopASdfghJKLZXCvbn02' }];
        this.groups = [{ id: 1, name: 'Tutorial Group' }];
        this.protocols = ["tcp", "http", "https", "dns", "icmp"];
      }

      const currentOs = (this.formField.operating_system || '').toLowerCase() || 'windows';

      let steps;
      if (section) {
        const builder = ALLOY_SECTIONS[section];
        steps = builder ? builder({ os: currentOs }) : [];
      } else if (type === 'alloyconfig') {
        steps = alloyFullSteps({ isStaff: this.isStaff, os: os || currentOs });
      } else {
        steps = collectorSteps();
      }

      steps = this.isStaff ? steps : steps.filter(item => !item.isStaff);
      if (!steps.length) return;

      await this._start(steps, () => {
        this.orgs = snapshot.orgs;
        this.groups = snapshot.groups;
        this.agents = snapshot.agents;
        this.protocols = snapshot.protocols;
        this.formField = { ...snapshot.formField };
        this.collectorConfigField = snapshot.collectorConfigField;
        this.workspaceConfigField = snapshot.workspaceConfigField;
        this.launcherConfigField = snapshot.launcherConfigField;
        this.alloyConfigField = snapshot.alloyConfigField;
        this.machineResponseField = snapshot.machineResponseField;
        this.databaseConfigField = snapshot.databaseConfigField;
        this.newSyntheticTransactions = snapshot.newSyntheticTransactions;
      });
    },

    onProtocolChange(newProtocol) {
      const currentName = this.collectorConfigField.name;
      const currentId = this.collectorConfigField.id;
      const currentTags = this.collectorConfigField.tags;
      const currentEnabled = this.collectorConfigField.enabled;

      if (newProtocol === 'dns') {

        this.collectorConfigField = {
          ...this.defaultDNSqueryConfig(),
          id: currentId,
          name: currentName,
          protocol: newProtocol,
          tags: currentTags,
          enabled: currentEnabled
        };
        
      }
      else if (newProtocol === 'http' || newProtocol === 'https') {
        this.collectorConfigField = {
          ...this.defaultHttpConfig(),
          id: currentId,
          name: currentName,
          protocol: newProtocol,
          tags: currentTags,
          enabled: currentEnabled
        };
        
      }
      else if (newProtocol === 'icmp') {
        this.collectorConfigField = {
          ...this.defaultPingConfig(),
          id: currentId,
          name: currentName,
          protocol: newProtocol,
          tags: currentTags,
          enabled: currentEnabled
        };
        
      } else {
        this.collectorConfigField = {
          ...this.defaultTCPConfig(),
          id: currentId,
          name: currentName,
          protocol: newProtocol,
          tags: currentTags,
          enabled: currentEnabled
        };
      }
      
      // Clear field errors when switching
      this.fieldErrors = {};
    },

    handleShowHowToPage(value){
      this.showHowToPage = value;
    },


    initializeOriginalValues() {
      this.originalConfigData = JSON.parse(JSON.stringify(this.prepareConfigData()));
      this.originalWorkspaceConfigField = JSON.parse(JSON.stringify(this.workspaceConfigField));
      this.originalMachineResponseField = JSON.parse(JSON.stringify(this.machineResponseField));
      this.originalCollectorMetricsToggle = JSON.parse(JSON.stringify(this.workspaceMetricsToggleForCollector));
      this.originalnewSyntheticTransactions = JSON.parse(JSON.stringify(this.newSyntheticTransactions));
      this.originalupdatedSyntheticTransactions = JSON.parse(JSON.stringify(this.updateSyntheticTransactions));
      this.originalconfigedSyntheticTransactions = JSON.parse(JSON.stringify(this.configedSyntheticTransactions));
      this.originalDeleteTransactions = JSON.parse(JSON.stringify(this.deletedSyntheticTransactions));
      this.originalDatabaseConfig = JSON.parse(JSON.stringify(this.databaseConfigField));
      this.originalLauncherConfig = JSON.parse(JSON.stringify(this.launcherConfigField));
      this.originalAlloyConfig = JSON.parse(JSON.stringify(this.alloyConfigField));

    },
    
    hasCollectorMetricsToggleChanged(){
      return !this.deepEqual(
        this.workspaceMetricsToggleForCollector,
        this.originalCollectorMetricsToggle
      );
    },

    hasConfigDataChanged() {
      const currentData = this.prepareConfigData();
      return !this.deepEqual(currentData, this.originalConfigData);
    },

    hasWorkspaceConfigChanged() {
      return !this.deepEqual(this.workspaceConfigField, this.originalWorkspaceConfigField);
    },


    hasLauncherConfigChanged() {
      return !this.deepEqual(this.launcherConfigField, this.originalLauncherConfig);
    },

    hasAlloyConfigChanged() {
      return !this.deepEqual(this.alloyConfigField, this.originalAlloyConfig);
    },

    hasInputPluginChanged() {
      return !this.deepEqual(this.machineResponseField, this.originalMachineResponseField);
    },

    hasDatabaseConfigChanged(){
      return !this.deepEqual(this.databaseConfigField, this.originalDatabaseConfig);
    },

    haveSyntheticTransactionsChanged(){
      return !this.deepEqual(this.updateSyntheticTransactions, this.originalupdatedSyntheticTransactions);
    },

    haveconfigedSynthenticTransactionsChanged(){
      return !this.deepEqual(this.configedSyntheticTransactions, this.originalconfigedSyntheticTransactions);
    },

    hasDeletedTransactions(){
      return !this.deepEqual(this.deletedSyntheticTransactions, this.originalDeleteTransactions);
    },

    hasNewSynthenticTransactions(){
       return !this.deepEqual(this.newSyntheticTransactions, this.originalnewSyntheticTransactions);
    },
    
    deepEqual(obj1, obj2) {
      if (obj1 === obj2) return true;
      
      if (obj1 == null || obj2 == null) return false;
      
      if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return obj1 === obj2;
      
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      
      if (keys1.length !== keys2.length) return false;
      
      for (let key of keys1) {
        if (!keys2.includes(key)) return false;
        if (!this.deepEqual(obj1[key], obj2[key])) return false;
      }
      
      return true;
    },

    checkPermissions(){
      this.canChangeConfig = useStore.hasPermission("Can change configs");
      this.canAddConfig = useStore.hasPermission("Can add configs");
    },

    defaultAlloyConfig() {
      return {
        config: null,
        log_level: "info",
        log_format: "logfmt",
        enabled_collectors: this.defaultWindowsCollectors(),
        proxy_url: "",
        proxy_username: "",
        proxy_password: "",
        otlp_endpoint: "",
        remote_write_org_id: "",
        scrape_interval_seconds: 60,
        log_file_paths: ["C:/Logs/*.log", "C:/ProgramData/Mivu/Logs/*.txt"],
        self_monitoring_enabled: true,

        // Windows-only
        collector_iis_enabled: false,
        collector_mssql_enabled: false,
        collector_dns_enabled: false,
        collector_dfsr_enabled: false,
        eventlog_system_enabled: true,
        eventlog_application_enabled: true,
        eventlog_xpath_query: "*[System[(Level=1 or Level=2 or Level=3)]]",

        // Windows Serverspace monitoring modules
        // IIS metrics collector + W3C access-log pipeline
        iis_enabled: false,
        iis_site_include: ".*",
        iis_site_exclude: "",
        iis_app_include: ".*",
        iis_app_exclude: "",
        iis_log_enabled: false,
        iis_log_path: "C:/inetpub/logs/LogFiles/W3SVC*/u_ex*.log",
        iis_log_metrics_enabled: true,
        // Hyper-V / Active Directory
        hyperv_enabled: false,
        ad_enabled: false,

        // Linux-only
        collector_systemd_enabled: true,
        collector_processes_enabled: false,
        journal_enabled: true,
        journal_max_age: "12h",
        journal_units: [],

        // Linux Serverspace monitoring modules 
        // eBPF zero-touch app monitoring (Beyla)
        ebpf_enabled: false,
        ebpf_app_name: "app",
        ebpf_open_ports: "8080,8443",
        ebpf_service_graph: false,
        ebpf_instrumentations: '["http", "sql"]',
        ebpf_routes_unmatched: "path",
        ebpf_log_path: "/var/log/app/access.log",
        // Apache
        apache_enabled: false,
        apache_status_port: 80,
        apache_log_enabled: false,
        apache_log_path: "/var/log/apache2/access.log",
        // MSSQL
        mssql_enabled: false,
        mssql_connection_string: "",
        mssql_connection_string_set: false,
        mssql_max_idle_connections: 3,
        mssql_max_open_connections: 3,
        mssql_timeout: "10s",
        // Postgres
        postgres_enabled: false,
        postgres_dsn: "",
        postgres_dsn_set: false,
        postgres_stat_statements: true,
        postgres_stat_user_tables: true,
        // VMware (vCenter / ESXi)
        vmware_enabled: false,
        vmware_endpoint: "",
        vmware_username: "",
        vmware_password: "",
        vmware_password_set: false,
        vmware_collection_interval: "60s",
        vmware_tls_insecure: true,
        vmware_vsan_enabled: false,
        vmware_per_vm_detail_enabled: true,
        vmware_extended_vm_perf: false,
      };
    },

    defaultWindowsCollectors() {
      return ["os", "service", "tcp", "terminal_services", "scheduled_task", "thermalzone", "time"];
    },

    defaultLinuxCollectors() {
      return ["cpu", "loadavg", "meminfo", "diskstats", "filesystem", "netdev", "netstat", "uname", "vmstat"];
    },
    applyAlloyOsDefaults() {
      if (!this.isNewConfig) return;
      if (this.isLinux) {
        this.alloyConfigField.enabled_collectors = this.defaultLinuxCollectors();
        this.alloyConfigField.log_file_paths = ["/var/log/*.log", "/var/log/**/*.log"];
      } else {
        this.alloyConfigField.enabled_collectors = this.defaultWindowsCollectors();
        this.alloyConfigField.log_file_paths = ["C:/Logs/*.log", "C:/ProgramData/Mivu/Logs/*.txt"];
      }
    },

    defaultLauncherConfig() {
      return {
        register_url: null,
        checkin_url: null,
        update_url: null,
        mimir_url: null,
        workspace: null,
        workspace_config: null,
        collector: null,
        collector_config: null,
        alloy: null,
        alloy_config: null,
        launcher_config: null,
        backup_dir: null,
        agent_license_key_file: null,
        org_license_key_file: null,
        token_file: null,
        checkin_interval: null,
        group_id: "",
        proxy_url: "",
      };
    },

    defaultWorkspaceConfig(){
      return {
        config: null,
        crit_host: process.env.VUE_APP_WORKSPACE_CRITICAL_HOST_URL,
        crit_proto: "tcp",
        gatewayping_bool: false,
        gatewayping_ip: null,
        polling_bool: true,
        wifi_bool: false,
        activity_bool: false,
        content_bool: false,
        disclosed_mode: false,
        activity_interval: 30,
        idle_threshold: 300
      }
    },

    applyActivityRules(data) {
      if (!data.activity_bool) {
        data.content_bool = false;
        data.disclosed_mode = false;
      }
      return data;
    },

    defaultTCPConfig() {
      return {
        id: null,
        name: null,
        protocol: null,
        address: null,
        tags: null,
        enabled: true        
      }
    },


    defaultDNSqueryConfig(){
      return {
        servers: [],
        domains: [],
        timeout: 2,
        tags: null,
        enabled: true
      }
    },

    defaultHttpConfig(){
      return {
        urls: [],
        timeout: 2
      }
    },

    defaultPingConfig(){
      return {
        urls: [],
        timeout: 2
      }
    },

    defaultMachineResponse(){
      return {
        process_bool: false,
        processor_bool: false,
        logical_disk_bool: false,
        physical_disk_bool: false,
        network_interface_bool: false,
        system_bool:false,
        memory_bool: false,
        paging_file_bool: false,
        docker_bool: false,
        systemd_bool: false,

        // Windows WMI / hardware inventory blocks
        battery_bool: false,
        logical_disk_inventory_bool: false,
        thermal_zone_bool: false,
        disk_drive_bool: false,
        location_bool: false,
        network_adapter_config_bool: false,
        computer_system_bool: false,
        operating_system_bool: false,
        bios_bool: false,
        physical_memory_bool: false,
        processor_info_bool: false,
        pnp_driver_bool: false
      }
    },

    defaultDatabaseConfig(){
      return {
        timeout: 59,
        username: null,
        urls: [],
        database: null,
        password: null,
        insecure_skip_verify: true,
        enabled: false,
      }
    },

    async fetchCollectorMetricsToggle(){
      try {
        const response = await getMetricsToggle();
        const collectorId = this.$route.params.id;
        const collectorToggle = response.find(item => item.config == collectorId);

        this.workspaceMetricsToggleForCollector = collectorToggle ||
        this.workspaceMetricsToggleForCollector;   
      } catch (error) {
        console.error(error);
      }
    },

    async updateCollectorMetricsToggle(){
      try {
        const collectorId = this.$route.params.id;
        const toggleData = this.workspaceMetricsToggleForCollector;
        if (toggleData.id){
          await updateMetricsToggle(toggleData.id, toggleData);
        }
        else{
          await createMetricsToggle({
            ...toggleData,
            config: collectorId
          });
        }
      } catch (error) {
        console.error('Error updating collector metrics toggle:', error);
      }
    },

    async fetchWorkspaceMetricsToggle() {
      try {
        const response = await getMetricsToggle();
        const workspaceId = this.$route.query.workspace;
        const workspaceToggle = response.find(item => item.config == workspaceId);

        this.workspaceMetricsToggleForWorkspace =  workspaceToggle 
        || this.workspaceMetricsToggleForWorkspace;
      } catch (error) {
        console.error('Error updating workspace metrics toggle:', error);
      }
    },

    async updateWorkspaceMetricsToggle() {
      try {
        const workspaceId = this.$route.query.workspace;
        const toggleData = this.workspaceMetricsToggleForWorkspace;
        if (toggleData.id) {
          await updateMetricsToggle(
            toggleData.id, 
            toggleData
          );
        } else {
          await createMetricsToggle({
            ...toggleData, 
            config: workspaceId
          });
        }
      } catch (error) {
        console.error(error);
      }
    },

    async syncBothToggles() {
      try {        
        this.workspaceMetricsToggleForWorkspace.enabled =
        this.workspaceMetricsToggleForCollector.enabled;
      } catch (error) {
        console.error(error);
      }
    },

    async loadConfigTypeSpecificData(type){
      switch (type) {
        case "workspaceconfig":
          await this.initialiseWorkspace();
          break;

        case "launcherconfig":
          await this.initialiseLauncher();
          break;

        case "alloyconfig":
          await this.initialiseAlloy();
          break;

        default:
          // Workspace and Collector use the same view but different models
          await this.initialiseWorkspace();
          await this.initialiseCollector();
          break;
      }   
    },

    async initialiseWorkspace(){
      await this.fetchWorkspaceConfig();
      Object.assign(this.workspaceConfigField, this.dataConfigWS);
    },

    async initialiseLauncher(){
      await this.fetchLauncherConfig();
      Object.assign(this.launcherConfigField, this.escapeBackslashesInObject(this.dataConfig));
    },

    async initialiseAlloy() {
      await this.fetchAlloyConfig();
    },

    async fetchAlloyConfig() {
      try {
        const response = await getAlloyConfigs(this.osType);
        const configId = this.$route.params.id;
        const match = response.find(item => item.config == configId);
        if (match) {
          Object.assign(this.alloyConfigField, match);
        }
      } catch (error) {
        console.error("Failed to fetch alloy config:", error);
      }
    },

    // Build the alloy-config payload, dropping any write-only secret left blank.
    buildAlloyPayload(configId) {
      const data = { ...this.alloyConfigField, config: configId };
      const clearProxyPassword = !!data.proxy_password_clear;
      delete data.proxy_password_clear;
      ["postgres_dsn", "mssql_connection_string", "vmware_password", "proxy_password"].forEach((key) => {
        if (!data[key]) delete data[key];
      });
      // "Clear stored password" 
      if (clearProxyPassword && !data.proxy_password) data.proxy_password = "";
      return data;
    },

    async saveAlloyConfig() {
      try {
        const configId = this.$route.params.id;
        const data = this.buildAlloyPayload(configId);
        if (data.id) {
          await updateAlloyConfig(data.id, data, this.osType);
        } else {
          await createAlloyConfig(data, this.osType);
        }
        delete this.alloyConfigField.proxy_password_clear;
      } catch (error) {
        this.errorManager(error);
      }
    },

    async initialiseCollector(){
      await this.fetchNetResponseConfig();
      await this.fetchInputPlugin();
      const response = await this.fetchDatabaseConfig();
      this.collectorConfigField = {
          ...this.defaultTCPConfig(),
          ...this.defaultDNSqueryConfig(),
          ...this.defaultHttpConfig(),
          ...this.defaultPingConfig()
      };
      Object.assign(this.machineResponseField, this.inputPlugin);
      Object.assign(this.databaseConfigField, this.databaseConfig);
    },

    escapeBackslashesInObject(obj) {
      const escaped = {}
      for (const [key, val] of Object.entries(obj)) {
        escaped[key] = typeof val === 'string'
          ? val.replace(/(?<!\\)\\(?!\\)/g, '\\\\') 
          : val
      }
      return escaped;
    },

    hideNetResponse(){
      this.displayNetResponse = false;
      this.collectorConfigField = {
          ...this.defaultTCPConfig(),
          ...this.defaultDNSqueryConfig(),
          ...this.defaultHttpConfig(),
          ...this.defaultPingConfig()
      };
      this.fieldErrors = {};
    },

    async deleteNetResponseItem(){
      try {
        if (this.formField.config_type === `collectorconfig`){
          const data = {...this.collectorConfigField}
          this.deletedSyntheticTransactions.push(data)
          this.netresponses = this.netresponses.filter(item => item.id !== this.selectedNetResponse.id);
          this.displayNetResponse = false;
          this.collectorConfigField = {
          ...this.defaultTCPConfig(),
          ...this.defaultDNSqueryConfig(),
          ...this.defaultHttpConfig(),
          ...this.defaultPingConfig()
      };
        } 
      } catch (error) {
        console.error(error);
      }
    },
    
    editNetResponse(netResponseID) {

      const canEdit = !this.isNewConfig && this.workspaceMetricsToggleForCollector.enabled;

      if (canEdit) {
        this.displayNetResponse = true; 
 
        this.selectedNetResponse = this.netresponses.find(
          item => item.id === netResponseID || item.tempId === netResponseID
        );
        
        if (!this.selectedNetResponse) {
          return;
        }
        
        this.isdropDownVisible = false;

        Object.assign(this.collectorConfigField, {
          ...this.selectedNetResponse,
          id: this.selectedNetResponse.id,
          tempId: this.selectedNetResponse.tempId
        });
      }
    },

    toggleConfig(){
      this.displayConfig = !this.displayConfig;
    },

    toggleTransaction(transaction){
      const data = this.netresponses.find(item => item.id == transaction);
      data["enabled"] = !data.enabled;
      this.updateSyntheticTransactions.push(data);
    },

    async fetchWorkspaceConfig(){
      try {
        const response = await getWorkspaceConfigs();
        const workspaceId = this.$route.query.workspace;
        this.dataConfigWS = {...response.find(item => item.config == workspaceId)};
      } catch (error) {
        console.error(error);
      }
    },

    async saveWorkspaceConfig(){
      try { 
        const data = this.applyActivityRules({
          ...this.workspaceConfigField,
          config: this.$route.query.workspace
        });

        Object.keys(data).forEach(key => {
          if (data[key] === '') {
            data[key] = null;
          }
        });

        if (data.id) {
          await updateWorkspaceConfig(data.id,data);
        } else {
          await createWorkspaceConfig(data);
        }
      } catch (error) {
        this.errorManager(error);
      }
    },

    async fetchLauncherConfig() {
      try {
        const response = await getLauncherConfigs();
        const configId = this.$route.params.id;
        this.dataConfig = {
          ...response.find(item => item.config == configId)
        };
      } catch (error) {
        console.error("Failed to fetch launcher config:", error);
      }
    },

    async saveLauncherConfig() {
      try {
        const configId = this.$route.params.id;
        const data = { 
          ...this.launcherConfigField, 
          config: configId, 
          group_id: this.launcherConfigField.group_id === '' ? null : this.launcherConfigField.group_id 
        };

        const isUpdate = Boolean(data.id);
        if (isUpdate) {
          await updateLauncherConfig(data.id, data);
        } else {
          await createLauncherConfig(data);
        }
      } catch (error) {
        this.errorManager(error);
      }
    },

    async fetchNetResponseConfig() {
      try {
        const response = await getNetResponses();
        this.netresponses = [
          ...response.filter(item => item.config == this.$route.params.id)
        ];
      } catch (error) {
        console.error("Failed to fetch net response config:", error);
      }
    },

    validateFields(fields, fieldsToCheck) {
      const errors = {};
      for (const fieldName of fieldsToCheck) {
        const value = fields[fieldName];
        if (!value || (typeof value === "string" && !value.trim()) || 
        (value instanceof Array && value.length === 0)) {
          errors[fieldName] = "This field cannot be empty";
        }
        if (value === "" ){

        }

      }
      return errors;
    },


    isSameTransaction(item, editingItem) {
      if (item.tempId && editingItem.tempId) {
        return item.tempId === editingItem.tempId;
      }
    },

    async saveNetResponseConfig() {
      try {
        const configId = this.$route.params.id;
        const data = {
          ...this.collectorConfigField,
          config: configId,
          pending: true,
          tempId: Date.now(),
        };
        
        this.validateSyntheticTransactions(data);
        if (Object.keys(this.fieldErrors).length > 0) return;

        const isUpdate = Boolean(data.id);
        const configExists = configId && configId !== 'new';

        if (isUpdate) {
          this.updateNetResponses(data);
          this.updateOrAddToList(this.updateSyntheticTransactions, data, item => item.id === data.id);
        } else {
          const existingIndex = this.findTransactionIndex(data);
          
          if (existingIndex !== -1) {
            this.netresponses.splice(existingIndex, 1, data);
            
            if (configExists) {
              this.updateOrReplaceInList(this.configedSyntheticTransactions, data, existingIndex);
            } else {
              this.updateOrReplaceInList(this.newSyntheticTransactions, data, existingIndex);
            }
          } else {
            this.netresponses.push(data);
            
            if (configExists) {
              this.configedSyntheticTransactions.push(data);
            } else {
              this.newSyntheticTransactions.push(data);
            }
          }
        }

        this.netresponses = this.deduplicateTransactions(this.netresponses);
        
        this.displayNetResponse = false;

        this.collectorConfigField = {
          ...this.defaultTCPConfig(),
          ...this.defaultDNSqueryConfig(),
          ...this.defaultHttpConfig(),
          ...this.defaultPingConfig()
        };

      } catch (error) {
        this.errorManager(error);
      }
    },


    updateNetResponses(data) {
      const index = this.netresponses.findIndex(item => item.id === data.id);
      if (index !== -1) {
        this.netresponses.splice(index, 1, data);
      } else {
        this.netresponses.push(data);
      }
    },

    updateOrAddToList(list, data, matchFn) {
      const index = list.findIndex(matchFn);
      if (index !== -1) {
        list.splice(index, 1, data);
      } else {
        list.push(data);
      }
    },

    findTransactionIndex(data) {
      if (this.collectorConfigField.tempId) {
        const tempIdMatch = this.netresponses.findIndex(
          item => item.tempId === this.collectorConfigField.tempId
        );
        if (tempIdMatch !== -1) return tempIdMatch;
      }
      
      return this.netresponses.findIndex(
        item => item.pending && this.isSameTransaction(item, this.collectorConfigField)
      );
    },

    updateOrReplaceInList(list, data, netResponsesIndex) {
      let listIndex = -1;
      
      if (this.collectorConfigField.tempId) {
        listIndex = list.findIndex(item => item.tempId === this.collectorConfigField.tempId);
      }
      
      if (listIndex === -1) {
        listIndex = list.findIndex(item => this.isSameTransaction(item, this.collectorConfigField));
      }
      
      if (listIndex !== -1) {
        list.splice(listIndex, 1, data);
      } else {
        list.push(data);
      }
    },

    deduplicateTransactions(transactions) {
      const uniqueMap = new Map();
      
      transactions.forEach(item => {

        const key = item.id 
          || (item.tempId ? `temp_${item.tempId}` : null)
          || `composite_${item.name}_${item.protocol}_${item.target || ''}_${item.port || ''}`;
        
        uniqueMap.set(key, item);
      });
      
      return Array.from(uniqueMap.values());
    },


    async updateSyntheticTransaction(){
      try {

        const results = await Promise.allSettled(
          this.updateSyntheticTransactions.map(response => 
            updateNetResponse(response.id, response)
          )
        );
        
        const failed = [];
        const succeeded = [];

        results.forEach((result, index) => {
          const transaction = this.updateSyntheticTransactions[index];
          
          if (result.status === 'rejected') {
            const errorDetail = result.reason?.response?.data?.detail 
              || result.reason?.response?.data?.message
              || result.reason?.message 
              || 'Unknown error';
            
            failed.push({
              transaction,
              error: errorDetail
            });
          } else {
            this.netresponses = this.netresponses.filter(item => item?.tempId !== transaction.tempId);
            this.netresponses.push(result.value);
            succeeded.push(transaction);
          }
        });

        this.updateSyntheticTransactions = failed.map(f => f.transaction);
        
        if (failed.length > 0) {
          const errorMessages = failed.map(f => 
            `${f.transaction.name || f.transaction.protocol || 'Transaction'}: ${f.error}`
          );
          
          this.validationErrorMessages = errorMessages
          this.validationFailed = true;
          
          console.error('Failed transactions:', failed);
        } else {
          this.updateSyntheticTransaction = [];
        }
      } catch (error) {
        this.validationMessage = error.response.data.detail;
        this.validationFailed = true;
      }
    },


    async createSyntheticTransactions() {
      try {
        const responsePromises = this.configedSyntheticTransactions.map(response => 
          createNetResponse(response)
        );

        const results = await Promise.allSettled(responsePromises);

        const failed = [];
        const succeeded = [];

        results.forEach((result, index) => {
          const transaction = this.configedSyntheticTransactions[index];
          if (result.status === 'rejected') {
            const errorDetail = result.reason?.response?.data?.detail 
              || result.reason?.response?.data?.message
              || result.reason?.message 
              || 'Unknown error';
            
            failed.push({
              transaction,
              error: errorDetail
            });
          } else {
            this.netresponses = this.netresponses.filter(item => item?.tempId !== transaction.tempId);
            this.netresponses.push(result.value);
            succeeded.push(transaction);
          }
        });

        this.configedSyntheticTransactions = failed.map(f => f.transaction);
        
        if (failed.length > 0) {
          const errorMessages = failed.map(f => 
            `${f.transaction.name || f.transaction.protocol || 'Transaction'}: ${f.error}`
          );
          
          this.validationErrorMessages = errorMessages
          this.validationFailed = true;
          
          console.error('Failed transactions:', failed);
        } else {
          this.configedSyntheticTransactions = [];
        }

      } catch (error) {
        console.error('Error creating net responses:', error);
        this.validationMessage = error.response?.data?.detail 
          || error.response?.data?.message
          || error.message 
          || 'An unexpected error occurred';
        this.validationFailed = true;
      }
    },

    async deleteSyntheticTransactions(){
      try {
        const responsePromises = this.deletedSyntheticTransactions.map(response => 
        deleteNetResponse(response.id, response)
        );

        await Promise.all(responsePromises);
        this.deletedSyntheticTransactions = [];
        
      } catch (error) {
          console.error('Error creating net responses:', error);
          this.validationMessage = error.response.data.detail;
          this.validationFailed = true;
      }
    },


    validateSyntheticTransactions(data){
      const protocolValidationRules = {
          dns: {
            fields: ["name", "protocol", "servers", "domains", "timeout"]
          },
          http: {
            fields: ["name", "protocol", "timeout", "urls"]
          },
          https: {
            fields: ["name", "protocol", "timeout", "urls"]
          },
          icmp: {
            fields: ["name", "protocol", "timeout", "urls"]
          },
          tcp: {
            fields: ["address", "name", "protocol"],
            customValidation: (data, fieldErrors) => {
              if (data.address) {
                const portMatch = data.address.match(/:(\d+)$/);
                
                if (!portMatch) {
                  fieldErrors.address = 'Address must include a port (e.g., google.com:443)';
                  return;
                }
                
                const port = parseInt(portMatch[1]);
                if (port < 1 || port > 65535) {
                  fieldErrors.address = 'Port must be between 1 and 65535';
                }
                if (/^[a-zA-Z]+[:;][\/\\]{0,2}/.test(data.address)){
                  fieldErrors.address = 'Address should not include http:// or https:// or any protocol';
                }
              }
            }
          },
          null: {
            fields: ["protocol"]
          }
        };

        const protocol = data.protocol;
        const rules = protocolValidationRules[protocol];

        if (!rules) {
          return;
        }

        this.fieldErrors = this.validateFields(data, rules.fields);

        if (rules.customValidation) {
          rules.customValidation(data, this.fieldErrors);
        }
    },

    saveSyntheticTransactions(){
      this.saveNetResponseConfig()
    },

    removeTransaction(item){
      this.netresponses = [...this.netresponses].filter(transaction => transaction !== item);
      this.configedSyntheticTransactions = this.configedSyntheticTransactions.filter(transaction => transaction !== item);
      this.newSyntheticTransactions = this.newSyntheticTransactions.filter(transaction => transaction !== item);
    },

    removeURL(url) {
      const index = this.databaseConfigField.urls.indexOf(url)
      if (index !== -1) {
        this.databaseConfigField.urls.splice(index, 1)
      }
    },

    async fetchInputPlugin() {
      try {
        const configId = this.$route.params.id;
        const response = await getMachineResponses();
        this.inputPlugin = response.find(item => item.config == configId) ||
        this.defaultMachineResponse();
      } catch (error) {
        console.error("Failed to fetch net response config:", error);
      }
    },

    async saveInputPlugin() {
      try {
        const configId = this.$route.params.id;
        const data = {
          ...this.machineResponseField,                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                             
          config: configId
        };

        const isUpdate = Boolean(data.id);

        if (isUpdate) {
          await updateMachineResponse(data.id, data);
        }  else {
          await createMachineResponse(data);
        }
      } catch (error) {
        this.errorManager(error);
      }
    },

    async fetchDatabaseConfig(){
      try {
        const configId = this.$route.params.id;
        const response = await getDatabaseConfigs();
        this.databaseConfig = response.find(item => item.config == configId) || 
        this.defaultDatabaseConfig();
      } catch (error) {
        this.errorManager(error);
      }
    },

    async saveDatabaseConfig() {
      try {
        const configId = this.$route.params.id;
        const data = {
          ...this.databaseConfigField,                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                             
          config: configId
        };

        const isUpdate = Boolean(data.id);

        if (isUpdate) {
          await updateDatabaseConfig(data.id, data);
        }  else {
          await createDatabaseConfig(data);
        }
      } catch (error) {
        this.validationFailed = true;
        this.errorManager(error);
      }
    },

    errorManager(error){
      this.fieldErrors = {};
      const errData = error?.response?.data;
      if (errData) {
        for (const key in errData) {
          const value = errData[key];

          if (Array.isArray(value)) {
            this.fieldErrors[key] = value[0];
          } 
          else if (typeof value === 'object' && value !== null) {
            const nestedFirstKey = Object.keys(value)[0];
            const nestedMessages = value[nestedFirstKey];

            if (Array.isArray(nestedMessages)) {
              this.fieldErrors[key] = nestedMessages[0];
            }
          }
        }
      }
    },
    async handleFileUpload(event) {
      this.clearFieldError('file');
      const file = event.target.files[0];
      if (!file) return;

      this.isProcessingFile = true;
      try {
        const result = await validateConfigFile(file);
        if (!result.ok) {
          this.fieldErrors = { ...this.fieldErrors, file: result.error };
          if (this.$refs.file) this.$refs.file.value = '';
          this.formField.file = null;
          return;
        }
        this.formField.file = file;
        this.isGeneralFileUpload = true;
      } finally {
        this.isProcessingFile = false;
      }
    },
    async handleWorkspaceFileUpload(event) {
      this.clearFieldError('workspace_file');
      const file = event.target.files[0];
      if (!file) return;

      const result = await validateConfigFile(file);
      if (!result.ok) {
        this.fieldErrors = { ...this.fieldErrors, workspace_file: result.error };
        if (event.target && 'value' in event.target) event.target.value = '';
        this.workspaceFile.file = null;
        return;
      }
      this.workspaceFile.file = file;
      this.isWorkspaceFileUpload = true;
    },
    organizationName(orgId){
      return this.orgs.find(org => org.id === orgId)?.name.toLowerCase();
    },
    async fetchOrg(){
      try {
        this.orgs = await getOrgs();
      } catch (error) {
        console.error("Error fetching organization:", error);
      }
    },
    async fetchAgents(){
      try {
        this.agents = await getAgents();
      } catch (error) {
        console.error("Error fetching agents:", error);
      }
    },
    async fetchGroups(){
      try {
        this.groups = await getAgentGroups();
      } catch (error) {
        console.error("Error fetching groups:", error);
      }
    },
    async fetchConfig(configId) {
      // Loading state is owned by created(), which gates the whole form.
      this.error = null;
      try {
        const configs = await getConfigs();
        this.formField = configs.find(item => item.id == configId);
        if (this.formField && !this.formField.space_type) {
          this.formField.space_type = "serverspace";
        }

      } catch (error) {
        console.error("Error fetching config:", error);
        this.error =
          "An error occurred while fetching config details. Please try again later.";
      }
    },
    async urlToFile(url, filename, mimeType) {
      const response = await fetch(url);
      const blob = await response.blob();
      return new File([blob], filename, { type: mimeType || blob.type });
    },
    async saveConfig() {

      this.validateConfigData();
      this.validateLauncherConfig();
      this.validateAlloyConfig();
      this.validateWorkspaceConfig();
      this.validateDatabaseConfig();
      if (Object.keys(this.fieldErrors).length > 0) return;

      this.error = null;
      try {
        const configData = this.prepareConfigData();
        const savePromises = [];

        this.isValidatingConfig = true;
        if(!this.isNewConfig) await this.handleDependentSaves(configData);

        if (configData.config_type === 'alloyconfig' && Object.keys(this.fieldErrors).length > 0) {
          this.isValidatingConfig = false;
          return;
        }


        if (this.isNewConfig){

          const mainConfigResponse = await createConfig(configData);
        
          let workspaceConfigResponse = null;

          if (mainConfigResponse.config_type === 'collectorconfig'){

            const workspaceConfigData = { ...configData};
            delete workspaceConfigData.file;

            const normalWorkspaceConfigData = {
              ...workspaceConfigData, 
              file: this.workspaceFile.file,
            }

            workspaceConfigResponse = await createConfig({
              ...normalWorkspaceConfigData,
              config_type: "workspaceconfig"
            });
          }
          
          if (this.shouldCreateAssociatedConfigs(mainConfigResponse, configData)) {
            await this.createAssociatedConfigs(mainConfigResponse, workspaceConfigResponse);
          }
        } 
        

        else { 
           if (this.hasConfigDataChanged()) {
              const needsFileConversion = !(configData.file instanceof File);

              if (needsFileConversion) {
                const fileToSend = await this.urlToFile(
                  this.formField.file,
                  this.formField.file
                );
                configData.file = fileToSend;
              }

              await updateConfig(this.formField.id, configData);
            }
          } 
        
        this.handleMultiStepBehaviour(configData);

      } catch (error) {
        this.errorManager(error);
        this.isValidatingConfig = false;
        console.error('Error creating/updating configs:', error);
      }
    },

    async handleDependentSaves(configData) {

      switch (configData.config_type) {
        case "collectorconfig":
          if (this.hasWorkspaceConfigChanged()) {
            await this.saveWorkspaceConfig();
          }

          if (this.haveSyntheticTransactionsChanged()){
            await this.updateSyntheticTransaction();
            if (this.validationFailed) return;
          }

          if (this.haveconfigedSynthenticTransactionsChanged()){
            await this.createSyntheticTransactions();
            if (this.validationFailed) return;
          }

          if (this.hasDeletedTransactions()){
            await this.deleteSyntheticTransactions();
            if (this.validationFailed) return;
          }

          if (this.hasInputPluginChanged()) {
            await this.saveInputPlugin();
          }

          if (this.isWorkspaceSpace && this.hasDatabaseConfigChanged()){
            await this.saveDatabaseConfig();
             if (this.validationFailed) return;
          }

          if (this.hasCollectorMetricsToggleChanged()){
            await this.updateCollectorMetricsToggle();
            await this.updateWorkspaceMetricsToggle();
          }
          break;

        case "workspaceconfig":
          if (this.hasWorkspaceConfigChanged()) {
            await this.saveWorkspaceConfig();
          }
          break;

        case "launcherconfig":
          if(!this.isGeneralFileUpload && this.hasLauncherConfigChanged()){
            await this.saveLauncherConfig();
          }
          break;

        case "alloyconfig":
          if (this.hasAlloyConfigChanged()) {
            await this.saveAlloyConfig();
          }
          break;
      }
    },


    handleMultiStepBehaviour(configData){
      if (this.isMultiSubmit && configData.config_type === 'collectorconfig'){
        this.$emit('collector-submitted',configData)
      }
      else if (this.isMultiSubmit && configData.config_type === 'workspaceconfig'){
        this.$emit('workspace-submitted',configData)
      }
      else if (this.isMultiSubmit && configData.config_type === 'alloyconfig'){
        this.$emit('collector-submitted', configData)
      }


      if(this.validationSuccessful && this.shouldShowSaveButton){
        this.$router.push({ name: "ConfigWorkSpaceView" });
      }
      
      if (!this.isMultiSubmit && !this.validationFailed){
        this.$router.push({ name: "ConfigWorkSpaceView" });
      }
    },

    prepareConfigData() {
      return {
        ...this.formattedConfig,
        agent: this.formattedConfig.agent || "",
        organization: this.formattedConfig.organization || "",
        group: this.formattedConfig.group || "",
      };
    },

    shouldCreateAssociatedConfigs(response, configData) {
      const hasId = Boolean(response?.id);
      return hasId;
    },

    async createAssociatedConfigs(mainConfigResponse, workspaceConfigResponse) {
      const configType = this.formField.config_type;
      
      switch (configType) {
        case 'workspaceconfig':
          await this.createWorkspaceOnlyConfig(mainConfigResponse.id);
          break;
          
        case 'launcherconfig':
          await this.createLauncherOnlyConfig(mainConfigResponse.id);
          break;

        case 'alloyconfig':
          await this.createAlloyOnlyConfig(mainConfigResponse.id);
          break;
          
        default:
          if (workspaceConfigResponse !== null){
            await this.createFullConfigStack(mainConfigResponse, workspaceConfigResponse);
          }
          break;
      }
    },

    async createWorkspaceOnlyConfig(configId) {
      if (this.isGeneralFileUpload) return;
      const data = this.applyActivityRules({
        ...this.workspaceConfigField,
        config: configId
      });
      await createWorkspaceConfig(data);
    },

    async createLauncherOnlyConfig(configId) {
      if (this.isGeneralFileUpload) return;
      const launcherData = {
        ...this.launcherConfigField,
        config: configId
      };
      if (!this.isGeneralFileUpload) await createLauncherConfig(launcherData);
    },

    async createAlloyOnlyConfig(configId) {
      const alloyData = this.buildAlloyPayload(configId);
      await createAlloyConfig(alloyData, this.osType);
    },


    async createFullConfigStack(mainConfigResponse, workspaceConfigResponse) {

      const hasCollectorFileUpload = Boolean(mainConfigResponse.file);
      const hasWorkspaceFileUpload = Boolean(workspaceConfigResponse.file);

      if (!hasWorkspaceFileUpload){
        await this.createWorkspaceConfig(workspaceConfigResponse.id);
      } 
     
      if (!hasCollectorFileUpload){
        await this.createCollectorAndResponses(mainConfigResponse.id);
      }
      
      await this.createMetricsToggle(mainConfigResponse.id, this.workspaceMetricsToggleForCollector),
      await this.createMetricsToggle(workspaceConfigResponse.id, this.workspaceMetricsToggleForCollector);

    },

    async createMetricsToggle(configId, toggleData) {
      const data = {
        ...toggleData,
        config: configId
      };
      return createMetricsToggle(data);
    },

    async createWorkspaceConfig(configId) {
      try {
        const workspaceData = this.applyActivityRules({
          ...this.workspaceConfigField,
          config: configId
        });
        await createWorkspaceConfig(workspaceData);
      } catch (error) {
        console.error(error)
      }
    },

    async createCollectorAndResponses(configId) {
      try {
        const promises = [];

        if (this.hasInputPluginChanged()) {
          const inputPlugin = {
            ...this.machineResponseField,
            config: configId
          };
          promises.push(createMachineResponse(inputPlugin));
        }

        if (this.isWorkspaceSpace && this.hasDatabaseConfigChanged()) {
          const databaseConfig = {
            ...this.databaseConfigField,
            config: configId
          };
          promises.push(createDatabaseConfig(databaseConfig));
        }

        if (this.hasNewSynthenticTransactions() && this.newSyntheticTransactions.length > 0) {
          const responsePromises = this.newSyntheticTransactions.map(response => 
            createNetResponse({ ...response, config: configId })
          );
          promises.push(...responsePromises);
        }

        if (promises.length > 0) {
          await Promise.all(promises);
        }

        this.newSyntheticTransactions = [];
      } catch (error) {
        console.error(error);
      }
    },


    async validateCollectorConfigFile() {
      try {
          this.isValidatingConfig = true;
          const configData = this.prepareConfigData();
          const needsFileConversion = !(configData.file instanceof File);

          this.shouldShowSaveButton = !needsFileConversion;

          if (needsFileConversion) {
            const fileToSend = await this.urlToFile(
              this.formField.file,
              this.formField.file
            );
            configData.file = fileToSend;
          }
          
          const response = await validateConfig(configData.file)

          this.validationSuccessful = response.status === "ok";
          this.validationFailed = !this.validationSuccessful;
          this.validationMessage = response.message

      } catch (error) {
          this.validationFailed = true;
          this.isValidatingConfig = false;
          this.validationMessage = error.response.data.message;
      } finally {
          this.isValidatingConfig = false;
      }
    },

    closeValidationModal() {
      this.validationSuccessful = false;
      this.validationFailed = false;
      this.isValidatingConfig = false;
      this.validationMessage = '';
      this.validationErrors = [];
    }
  },
};
</script>

